import { MoCommand } from "./command";

const EPSILON = 0.0001;

/**
 * Continuous directional movement command for WASD controls.
 * Unlike MoveToPosCmd which moves toward a fixed target,
 * this command moves in a direction that is updated externally each frame.
 * It never completes on its own — only via interrupt (wantInterrupt).
 */
export class WASDMoveCmd extends MoCommand {
    dirX: number;
    dirY: number;

    constructor(object: any, dirX: number, dirY: number) {
        super(object);
        this.dirX = dirX;
        this.dirY = dirY;
    }

    enter() {
        this.object.pawn.playRunMotion();
    }

    leave() {
        if (!this.object.nextCmd) {
            this.object.pawn.playIdleMotion();
        }
    }

    setDirection(dirX: number, dirY: number) {
        this.dirX = dirX;
        this.dirY = dirY;
    }

    update(delta: number) {
        if (this.wantInterrupt) {
            this.isComplete = true;
            return delta;
        }

        const thisObj = this.object;
        const thisPos = thisObj.position;

        const frameMoveSpeed = thisObj.moveSpeed * 0.01 * delta;

        const velocityX = this.dirX * frameMoveSpeed;
        const velocityY = this.dirY * frameMoveSpeed;

        if (velocityX * velocityX + velocityY * velocityY < EPSILON) {
            return 0;
        }

        this.object.direction =
            Math.atan2(this.dirY, this.dirX) + Math.PI / 2;

        const newPosition = thisPos.clone();
        newPosition.x += velocityX;
        newPosition.y += velocityY;

        const highPointInfo = thisObj.world.findHighPointWithInfo(
            newPosition.x,
            newPosition.y,
            newPosition.z + 1
        );

        const highZ = highPointInfo ? highPointInfo.z : undefined;
        const isTerrain = highPointInfo ? highPointInfo.isTerrain : false;

        const terrainMaxSlopeRatio = 1.3;
        const objectMaxStepHeight = 0.5;
        const objectSlopeRatio = 5.0;
        const gravity = 10;

        const heightDiff = highZ !== undefined ? highZ - thisPos.z : 0;
        const horizontalDist = Math.sqrt(
            velocityX * velocityX + velocityY * velocityY
        );
        const slopeRatio =
            horizontalDist > 0.001 ? heightDiff / horizontalDist : 0;

        let canClimb = false;
        if (heightDiff <= 0) {
            canClimb = true;
        } else if (isTerrain) {
            canClimb = slopeRatio <= terrainMaxSlopeRatio;
        } else {
            canClimb =
                heightDiff <= objectMaxStepHeight &&
                slopeRatio <= objectSlopeRatio;
        }

        if (
            !thisObj.useMoveCollision ||
            (highZ !== undefined && canClimb)
        ) {
            if (heightDiff < 0) {
                const fallAmount = Math.min(
                    Math.abs(heightDiff),
                    gravity * delta
                );
                newPosition.z = thisPos.z - fallAmount;
            } else {
                newPosition.z = highZ;
            }

            let canMove = true;
            if (
                thisObj.useMoveCollision &&
                thisObj.world.collisionDetector
            ) {
                canMove = thisObj.world.collisionDetector.checkCollision(
                    thisObj,
                    newPosition
                );
            }

            if (canMove) {
                thisObj.setPosition(
                    newPosition.x,
                    newPosition.y,
                    newPosition.z
                );
            }
        }

        // Never complete — continuous movement until interrupted
        return 0;
    }
}
